import { useEffect, useRef, useState } from 'react'
import type { FieldProps } from '@rjsf/utils'
import { Mtds } from 'prove-common'
import {
  type ElvenettRiver,
  isValidWgs84Coordinate,
  useGetRiversNearPointQuery
} from '../../../hooks/useElvenett'
import {
  areCoordinatesEqual,
  extractCoordinates
} from '../../../utils/sampleUtils'
import type { SampleFormContext, VassdragValue } from '../../../types/types'
import { requiredFieldMessage } from '../../../utils/requiredFieldMessage'

const collectErrorMessages = (errorSchema: unknown): string[] => {
  if (errorSchema == null || typeof errorSchema !== 'object') return []

  const schema = errorSchema as Record<string, unknown>
  const ownErrors = Array.isArray(schema.__errors)
    ? schema.__errors.filter(
        (error): error is string => typeof error === 'string'
      )
    : []
  const nestedErrors = Object.entries(schema).flatMap(([key, value]) =>
    key === '__errors' ? [] : collectErrorMessages(value)
  )

  return [...ownErrors, ...nestedErrors]
}

const getFieldErrorMessages = (
  rawErrors: string[] | undefined,
  errorSchema: unknown,
  title: string | undefined
) => {
  if (rawErrors && rawErrors.length > 0) return rawErrors

  const nestedErrors = collectErrorMessages(errorSchema)
  if (nestedErrors.length === 0) return []
  if (!title) return nestedErrors

  return [requiredFieldMessage(title)]
}

export function VassdragField(props: Readonly<FieldProps>) {
  const {
    id,
    formData,
    onChange,
    disabled = false,
    readonly = false,
    required = false,
    rawErrors,
    errorSchema,
    schema,
    fieldPathId,
    formContext: directFormContext,
    registry
  } = props

  const typedFormData = formData as VassdragValue | undefined

  const formContext =
    directFormContext ??
    (registry?.formContext as SampleFormContext | undefined)

  const path = fieldPathId?.path
  const fieldId = id || fieldPathId?.$id || 'vassdrag'

  const coords = extractCoordinates(formContext?.position)
  const hasValidPosition = isValidWgs84Coordinate(
    coords?.longitude,
    coords?.latitude
  )

  const isInteractive = !disabled && !readonly

  const {
    data: rivers,
    isLoading,
    isError,
    refetch
  } = useGetRiversNearPointQuery(coords?.longitude, coords?.latitude, {
    enabled: isInteractive && hasValidPosition
  })

  const [positionChangedNotice, setPositionChangedNotice] = useState<
    string | null
  >(null)
  const [manualModeSelected, setManualModeSelected] = useState(false)

  const prevCoordsRef = useRef<
    { longitude: number; latitude: number } | undefined
  >(
    hasValidPosition
      ? { longitude: coords!.longitude, latitude: coords!.latitude }
      : undefined
  )
  const isInitialMountRef = useRef(true)
  const pendingCoordinateChangeRef = useRef(false)
  const isNormalizingEmptyRef = useRef(false)

  const coordLon = coords?.longitude
  const coordLat = coords?.latitude

  const currentNummer = typedFormData?.vassdragsnummer ?? ''
  const currentNavn = typedFormData?.elvenavn ?? ''
  const hasValue = currentNummer.trim() !== ''
  const hasEnteredData = Boolean(
    (typedFormData?.vassdragsnummer != null &&
      typedFormData.vassdragsnummer !== '') ||
    (typedFormData?.elvenavn != null && typedFormData.elvenavn !== '')
  )
  const isNummerRequired = required || currentNavn.trim() !== ''

  // Deduplicate river segments sharing (vassdragsnummer, elvenavn)
  const uniqueRivers = (rivers ?? []).filter(
    (river, index, self) =>
      index ===
      self.findIndex(
        (r) =>
          r.vassdragsnummer === river.vassdragsnummer &&
          r.elvenavn === river.elvenavn
      )
  )

  useEffect(() => {
    if (!isInteractive || required) return

    const isEmptyObject =
      formData != null &&
      typeof formData === 'object' &&
      !formData.vassdragsnummer &&
      !formData.elvenavn

    if (isEmptyObject && !isNormalizingEmptyRef.current) {
      isNormalizingEmptyRef.current = true
      onChange(undefined, path)
    } else if (!isEmptyObject) {
      isNormalizingEmptyRef.current = false
    }
  }, [formData, required, isInteractive, onChange, path])

  useEffect(() => {
    const currentCoords =
      hasValidPosition && coordLon != null && coordLat != null
        ? { longitude: coordLon, latitude: coordLat }
        : undefined

    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      prevCoordsRef.current = currentCoords
      return
    }

    const hadPrev = prevCoordsRef.current != null
    const hasCurrent = currentCoords != null

    const coordsTrulyChanged =
      (hadPrev &&
        hasCurrent &&
        !areCoordinatesEqual(prevCoordsRef.current!, currentCoords)) ||
      (!hadPrev && hasCurrent) ||
      (hadPrev && !hasCurrent)

    if (coordsTrulyChanged) {
      if (!isInteractive) {
        pendingCoordinateChangeRef.current = true
        prevCoordsRef.current = currentCoords
        return
      }

      if (hasEnteredData) {
        onChange(undefined, path)
        setPositionChangedNotice(
          'Posisjonen er endret. Vennligst velg vassdrag på nytt.'
        )
        setManualModeSelected(false)
      }
      prevCoordsRef.current = currentCoords
      pendingCoordinateChangeRef.current = false
    }
  }, [
    coordLon,
    coordLat,
    hasValidPosition,
    isInteractive,
    hasEnteredData,
    onChange,
    path
  ])

  useEffect(() => {
    if (isInteractive && pendingCoordinateChangeRef.current) {
      if (hasEnteredData) {
        onChange(undefined, path)
        setPositionChangedNotice(
          'Posisjonen er endret. Vennligst velg vassdrag på nytt.'
        )
        setManualModeSelected(false)
      }
      pendingCoordinateChangeRef.current = false
    }
  }, [isInteractive, hasEnteredData, onChange, path])

  const matchingRiver = uniqueRivers.find(
    (river) =>
      river.vassdragsnummer === currentNummer && river.elvenavn === currentNavn
  )

  const isManual =
    manualModeSelected ||
    (hasValue && uniqueRivers.length > 0 && !matchingRiver) ||
    !hasValidPosition ||
    (rivers != null && rivers.length === 0) ||
    isError

  const handleSelectRiver = (river: ElvenettRiver) => {
    setManualModeSelected(false)
    setPositionChangedNotice(null)
    onChange(
      {
        vassdragsnummer: river.vassdragsnummer,
        elvenavn: river.elvenavn
      },
      path
    )
  }

  const handleSelectManualMode = () => {
    setManualModeSelected(true)
    setPositionChangedNotice(null)
  }

  const handleClear = () => {
    setManualModeSelected(false)
    setPositionChangedNotice(null)
    onChange(undefined, path)
  }

  const handleManualNummerChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPositionChangedNotice(null)
    const val = event.target.value
    if (val === '' && currentNavn === '' && !required) {
      onChange(undefined, path)
    } else {
      onChange(
        {
          vassdragsnummer: val,
          ...(currentNavn ? { elvenavn: currentNavn } : {})
        },
        path
      )
    }
  }

  const handleManualNavnChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPositionChangedNotice(null)
    const val = event.target.value
    if (currentNummer === '' && val === '' && !required) {
      onChange(undefined, path)
    } else {
      onChange(
        {
          vassdragsnummer: currentNummer,
          ...(val ? { elvenavn: val } : {})
        },
        path
      )
    }
  }

  const title = typeof schema?.title === 'string' ? schema.title : 'Vassdrag'
  const description =
    typeof schema?.description === 'string' ? schema.description : undefined

  const errorMessages = Array.from(
    new Set(getFieldErrorMessages(rawErrors, errorSchema, title))
  )

  if (disabled || readonly) {
    return (
      <Mtds.Fieldset id={fieldId} data-gap='3'>
        <Mtds.Fieldset.Legend>{title}</Mtds.Fieldset.Legend>
        {description && (
          <Mtds.Fieldset.Description>{description}</Mtds.Fieldset.Description>
        )}
        {hasValue ? (
          <Mtds.Grid data-gap='3'>
            <Mtds.Field
              as='input'
              label='Vassdragsnummer'
              value={currentNummer}
              disabled
              readOnly
            />
            <Mtds.Field
              as='input'
              label='Elvenavn'
              value={currentNavn || '-'}
              disabled
              readOnly
            />
          </Mtds.Grid>
        ) : (
          <Mtds.Muted>Ingen vassdrag registrert</Mtds.Muted>
        )}
      </Mtds.Fieldset>
    )
  }

  return (
    <Mtds.Fieldset id={fieldId} data-gap='4'>
      <Mtds.Fieldset.Legend>{title}</Mtds.Fieldset.Legend>
      {description && (
        <Mtds.Fieldset.Description>{description}</Mtds.Fieldset.Description>
      )}

      {positionChangedNotice && (
        <Mtds.Alert data-color='warning'>
          <p>{positionChangedNotice}</p>
        </Mtds.Alert>
      )}

      {!hasValidPosition && (
        <Mtds.Alert data-color='info'>
          <p>
            Posisjon mangler eller er ikke klar. Du kan taste inn vassdrag
            manuelt.
          </p>
        </Mtds.Alert>
      )}

      {hasValidPosition && isLoading && (
        <Mtds.Flex data-align='center' data-gap='3'>
          <Mtds.Spinner />
          <Mtds.Muted>Henter elver i nærheten...</Mtds.Muted>
        </Mtds.Flex>
      )}

      {hasValidPosition && isError && (
        <Mtds.Alert data-color='warning'>
          <Mtds.Heading as='h4'>Kunne ikke hente elver i nærheten</Mtds.Heading>
          <p>Det oppstod en feil ved oppslag mot elvenett.</p>
          <Mtds.Button
            type='button'
            data-variant='secondary'
            onClick={() => refetch()}
          >
            Prøv igjen
          </Mtds.Button>
        </Mtds.Alert>
      )}

      {hasValidPosition && !isLoading && !isError && rivers?.length === 0 && (
        <Mtds.Alert data-color='info'>
          <p>
            Ingen elver funnet i nærheten (innen 200 meter). Legg inn vassdrag
            manuelt.
          </p>
        </Mtds.Alert>
      )}

      {hasValidPosition &&
        !isLoading &&
        !isError &&
        rivers != null &&
        rivers.length > 0 && (
          <Mtds.Grid data-gap='3'>
            <Mtds.Muted>Elver innen 200 meter:</Mtds.Muted>
            {uniqueRivers.map((river, index) => {
              const isSelected =
                !isManual &&
                currentNummer === river.vassdragsnummer &&
                currentNavn === river.elvenavn
              const radioId = `${fieldId}_river_${index}_${encodeURIComponent(river.vassdragsnummer)}`
              return (
                <Mtds.Field
                  as='input'
                  type='radio'
                  id={radioId}
                  name={`${fieldId}_river_choice`}
                  key={`${river.vassdragsnummer}-${river.elvenavn}-${index}`}
                  label={`${river.elvenavn} – ${river.vassdragsnummer}`}
                  checked={isSelected}
                  onChange={() => handleSelectRiver(river)}
                />
              )
            })}
            <Mtds.Field
              as='input'
              type='radio'
              id={`${fieldId}_river_manual`}
              name={`${fieldId}_river_choice`}
              label='Annet vassdrag (legg inn manuelt)'
              checked={isManual}
              onChange={handleSelectManualMode}
            />
            {!required && (
              <Mtds.Field
                as='input'
                type='radio'
                id={`${fieldId}_river_clear`}
                name={`${fieldId}_river_choice`}
                label='Ikke oppgitt / ingen vassdrag'
                checked={!hasValue && !isManual}
                onChange={handleClear}
              />
            )}
          </Mtds.Grid>
        )}

      {isManual && (
        <Mtds.Grid data-gap='3'>
          <Mtds.Field
            as='input'
            id={`${fieldId}_manual_vassdragsnummer`}
            label='Vassdragsnummer'
            description='Bevar eventuelle ledende nuller og punktum'
            value={currentNummer}
            required={isNummerRequired}
            onChange={handleManualNummerChange}
          />
          <Mtds.Field
            as='input'
            id={`${fieldId}_manual_elvenavn`}
            label='Elvenavn (valgfritt)'
            value={currentNavn}
            onChange={handleManualNavnChange}
          />
        </Mtds.Grid>
      )}

      {errorMessages.map((errorMessage) => (
        <Mtds.Validation key={`${fieldId}-${errorMessage}`}>
          {errorMessage}
        </Mtds.Validation>
      ))}
    </Mtds.Fieldset>
  )
}
